import type {
  CanvasEdge,
  CanvasNode,
  DomainBlock,
  DomainCanvas,
  DomainDocument,
  JsonValue,
  VersionedEntity,
} from "@note-app/contracts";
import {
  CURRENT_CONTENT_SCHEMA_VERSION,
  classifyContentSchemaVersion,
} from "@note-app/contracts";

import {
  UnsupportedSchemaVersionError,
  ValidationError,
  type ValidationIssue,
} from "./errors.ts";

/** @deprecated Prefer CURRENT_CONTENT_SCHEMA_VERSION from @note-app/contracts. */
export const CURRENT_SCHEMA_VERSION = CURRENT_CONTENT_SCHEMA_VERSION;

const UUID_PATTERN =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-8][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

export function isUuid(value: string): boolean {
  return UUID_PATTERN.test(value);
}

export function assertUuid(value: string, path = "id"): void {
  if (!isUuid(value)) {
    throw new ValidationError([{ path, message: "must be an RFC 4122 UUID" }]);
  }
}

export function assertCurrentSchemaVersion(schemaVersion: number): void {
  if (schemaVersion !== CURRENT_SCHEMA_VERSION) {
    const compatibility = classifyContentSchemaVersion(schemaVersion);
    throw new UnsupportedSchemaVersionError(
      schemaVersion,
      CURRENT_SCHEMA_VERSION,
      compatibility === "future" ? "future" : "unsupported-old",
    );
  }
}

export function assertVersionedEntity(entity: VersionedEntity): void {
  const issues: ValidationIssue[] = [];
  if (!isUuid(entity.id)) {
    issues.push({ path: "id", message: "must be an RFC 4122 UUID" });
  }
  if (!Number.isSafeInteger(entity.revision) || entity.revision < 0) {
    issues.push({
      path: "revision",
      message: "must be a non-negative safe integer",
    });
  }
  validateTimestamp(entity.createdAt, "createdAt", issues);
  validateTimestamp(entity.updatedAt, "updatedAt", issues);
  if (entity.updatedAt < entity.createdAt) {
    issues.push({
      path: "updatedAt",
      message: "must not be earlier than createdAt",
    });
  }
  if (entity.deletedAt !== undefined) {
    validateTimestamp(entity.deletedAt, "deletedAt", issues);
    if (entity.deletedAt < entity.createdAt) {
      issues.push({
        path: "deletedAt",
        message: "must not be earlier than createdAt",
      });
    }
  }
  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
  assertCurrentSchemaVersion(entity.schemaVersion);
}

export function assertNewEntity(entity: VersionedEntity): void {
  assertVersionedEntity(entity);
  const issues: ValidationIssue[] = [];
  if (entity.revision !== 0) {
    issues.push({ path: "revision", message: "new entities must start at 0" });
  }
  if (entity.createdAt !== entity.updatedAt) {
    issues.push({
      path: "updatedAt",
      message: "new entities must have matching createdAt and updatedAt",
    });
  }
  if (entity.deletedAt !== undefined) {
    issues.push({
      path: "deletedAt",
      message: "new entities must not start in the trash",
    });
  }
  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
}

export function assertTimestampTransition(
  previous: number,
  next: number,
  path = "updatedAt",
): void {
  const issues: ValidationIssue[] = [];
  validateTimestamp(previous, "previousUpdatedAt", issues);
  validateTimestamp(next, path, issues);
  if (next < previous) {
    issues.push({ path, message: "must not move backwards" });
  }
  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
}

export function assertDocument(document: DomainDocument): void {
  if (!isRecord(document)) {
    throw new ValidationError([
      {
        code: "invalid-document",
        path: "document",
        message: "must be an object",
      },
    ]);
  }
  assertCurrentSchemaVersion(document.schemaVersion);
  const issues: ValidationIssue[] = [];
  if (!Array.isArray(document.blocks)) {
    throw new ValidationError([
      {
        code: "invalid-document",
        path: "blocks",
        message: "must be an array",
      },
    ]);
  }
  const ids = new Set<string>();
  document.blocks.forEach((block, index) =>
    validateBlock(block, `blocks[${index}]`, ids, issues),
  );
  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
}

export function assertCanvas(canvas: DomainCanvas): void {
  assertCurrentSchemaVersion(canvas.schemaVersion);
  const issues: ValidationIssue[] = [];
  const nodeIds = new Set<string>();
  const edgeIds = new Set<string>();
  if (!Array.isArray(canvas.nodes) || !Array.isArray(canvas.edges)) {
    throw new ValidationError([
      {
        code: "invalid-canvas",
        path: !Array.isArray(canvas.nodes) ? "nodes" : "edges",
        message: "must be an array",
      },
    ]);
  }
  if (
    canvas.mode !== "freeform" &&
    canvas.mode !== "mindmap" &&
    canvas.mode !== "research" &&
    canvas.mode !== "project"
  ) {
    issues.push({ path: "mode", message: "is not a supported canvas mode" });
  }

  canvas.nodes.forEach((node, index) =>
    validateCanvasNode(node, `nodes[${index}]`, nodeIds, issues),
  );
  const nodesById = new Map(canvas.nodes.map((node) => [node.id, node]));
  canvas.nodes.forEach((node, index) => {
    if (
      node.parentFrameId !== undefined &&
      !nodeIds.has(node.parentFrameId)
    ) {
      issues.push({
        path: `nodes[${index}].parentFrameId`,
        code: "dangling-frame",
        message: "must reference an existing node",
        entityId: node.id,
      });
    } else if (
      node.parentFrameId !== undefined &&
      nodesById.get(node.parentFrameId)?.type !== "frame"
    ) {
      issues.push({
        path: `nodes[${index}].parentFrameId`,
        code: "invalid-frame-type",
        message: "must reference a node whose type is frame",
        entityId: node.id,
      });
    }
    const visited = new Set<string>([node.id]);
    let parentId = node.parentFrameId;
    while (parentId !== undefined) {
      if (visited.has(parentId)) {
        issues.push({
          path: `nodes[${index}].parentFrameId`,
          code: "cyclic-frame",
          message: "must not create a frame membership cycle",
          entityId: node.id,
        });
        break;
      }
      visited.add(parentId);
      parentId = nodesById.get(parentId)?.parentFrameId;
    }
  });
  canvas.edges.forEach((edge, index) =>
    validateCanvasEdge(edge, `edges[${index}]`, edgeIds, nodeIds, issues),
  );
  validateJsonValue(canvas.settings, "settings", issues, new Set<object>());

  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
}

export function assertJsonValue(value: unknown, path = "value"): asserts value is JsonValue {
  const issues: ValidationIssue[] = [];
  validateJsonValue(value, path, issues, new Set<object>());
  if (issues.length > 0) {
    throw new ValidationError(issues);
  }
}

function validateBlock(
  block: DomainBlock,
  path: string,
  ids: Set<string>,
  issues: ValidationIssue[],
): void {
  if (!isRecord(block)) {
    issues.push({
      code: "invalid-block",
      path,
      message: "must be an object",
    });
    return;
  }
  if (typeof block.id === "string") {
    validateUniqueUuid(block.id, `${path}.id`, ids, issues);
  } else {
    issues.push({
      code: "invalid-id",
      path: `${path}.id`,
      message: "must be an RFC 4122 UUID",
    });
  }
  validateNonEmptyString(block.type, `${path}.type`, issues);
  validateJsonValue(block.props, `${path}.props`, issues, new Set<object>());
  validateJsonValue(block.content, `${path}.content`, issues, new Set<object>());
  if (!Array.isArray(block.children)) {
    issues.push({
      code: "invalid-block",
      path: `${path}.children`,
      message: "must be an array",
    });
  } else {
    block.children.forEach((child, index) =>
      validateBlock(child, `${path}.children[${index}]`, ids, issues),
    );
  }
}

function validateCanvasNode(
  node: CanvasNode,
  path: string,
  ids: Set<string>,
  issues: ValidationIssue[],
): void {
  validateUniqueUuid(node.id, `${path}.id`, ids, issues);
  validateNonEmptyString(node.type, `${path}.type`, issues);
  const geometryValues: ReadonlyArray<
    readonly [string, number]
  > = [
    ["x", node.geometry.x],
    ["y", node.geometry.y],
    ["width", node.geometry.width],
    ["height", node.geometry.height],
    ["rotation", node.geometry.rotation],
  ];
  for (const [name, value] of geometryValues) {
    if (!Number.isFinite(value)) {
      issues.push({
        path: `${path}.geometry.${name}`,
        message: "must be finite",
      });
    }
  }
  if (node.geometry.width <= 0 || node.geometry.height <= 0) {
    issues.push({
      path: `${path}.geometry`,
      code: "invalid-geometry",
      message: "width and height must be greater than zero",
      entityId: node.id,
    });
  }
  validateNonEmptyString(
    node.geometry.zIndex,
    `${path}.geometry.zIndex`,
    issues,
  );
  if (node.parentFrameId !== undefined && !isUuid(node.parentFrameId)) {
    issues.push({
      path: `${path}.parentFrameId`,
      message: "must be an RFC 4122 UUID",
    });
  }
  if (node.reference !== undefined) {
    if (
      node.reference.kind !== "item" &&
      node.reference.kind !== "block" &&
      node.reference.kind !== "attachment"
    ) {
      issues.push({
        path: `${path}.reference.kind`,
        message: "is not a supported reference kind",
      });
    }
    if (!isUuid(node.reference.id)) {
      issues.push({
        path: `${path}.reference.id`,
        message: "must be an RFC 4122 UUID",
      });
    }
  }
  if (
    node.readingOrder !== undefined &&
    (!Number.isSafeInteger(node.readingOrder) || node.readingOrder < 0)
  ) {
    issues.push({
      path: `${path}.readingOrder`,
      message: "must be a non-negative safe integer",
    });
  }
  validateJsonValue(node.props, `${path}.props`, issues, new Set<object>());
}

function validateCanvasEdge(
  edge: CanvasEdge,
  path: string,
  ids: Set<string>,
  nodeIds: Set<string>,
  issues: ValidationIssue[],
): void {
  validateUniqueUuid(edge.id, `${path}.id`, ids, issues);
  if (!nodeIds.has(edge.sourceNodeId)) {
    issues.push({
      path: `${path}.sourceNodeId`,
      code: "dangling-edge",
      message: "must reference an existing node",
      entityId: edge.id,
    });
  }
  if (!nodeIds.has(edge.targetNodeId)) {
    issues.push({
      path: `${path}.targetNodeId`,
      code: "dangling-edge",
      message: "must reference an existing node",
      entityId: edge.id,
    });
  }
  validateJsonValue(edge.props, `${path}.props`, issues, new Set<object>());
}

function validateJsonValue(
  value: unknown,
  path: string,
  issues: ValidationIssue[],
  ancestors: Set<object>,
): void {
  if (
    value === null ||
    typeof value === "string" ||
    typeof value === "boolean"
  ) {
    return;
  }
  if (typeof value === "number") {
    if (!Number.isFinite(value)) {
      issues.push({ path, message: "numbers must be finite" });
    }
    return;
  }
  if (typeof value !== "object") {
    issues.push({ path, message: "must be JSON-compatible" });
    return;
  }
  if (ancestors.has(value)) {
    issues.push({ path, message: "must not contain cycles" });
    return;
  }

  ancestors.add(value);
  if (Array.isArray(value)) {
    value.forEach((entry, index) =>
      validateJsonValue(entry, `${path}[${index}]`, issues, ancestors),
    );
  } else {
    const prototype = Object.getPrototypeOf(value) as object | null;
    if (prototype !== Object.prototype && prototype !== null) {
      issues.push({ path, message: "must contain only plain objects" });
    } else {
      Object.entries(value).forEach(([key, entry]) =>
        validateJsonValue(entry, `${path}.${key}`, issues, ancestors),
      );
    }
  }
  ancestors.delete(value);
}

function validateUniqueUuid(
  value: string,
  path: string,
  ids: Set<string>,
  issues: ValidationIssue[],
): void {
  if (!isUuid(value)) {
    issues.push({ path, message: "must be an RFC 4122 UUID" });
  } else if (ids.has(value)) {
    issues.push({
      path,
      code: "duplicate-id",
      message: "must be unique",
      entityId: value,
    });
  }
  ids.add(value);
}

function validateNonEmptyString(
  value: unknown,
  path: string,
  issues: ValidationIssue[],
): void {
  if (typeof value !== "string" || value.trim().length === 0) {
    issues.push({ path, message: "must not be empty" });
  }
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

function validateTimestamp(
  value: number,
  path: string,
  issues: ValidationIssue[],
): void {
  if (!Number.isSafeInteger(value) || value < 0) {
    issues.push({
      path,
      message: "must be a non-negative Unix timestamp in milliseconds",
    });
  }
}
